import { IndicatorStatus } from '@/models/IndicatorStatus.ts';
import { PatientAddress } from '@/models/Patient.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

export enum AppointmentBookStatus {
    PENDING_BOOKING = 'PENDING_BOOKING',
    BOOKED = 'BOOKED',
    CANCELLED = 'CANCELLED',
    COMPLETED = 'COMPLETED',
    DECLINED = 'DECLINED',
}

export enum AppointmentCancelReason {
    DOCTOR_CANCELLED = 'DOCTOR_CANCELLED',
    PATIENT_CANCELLED = 'PATIENT_CANCELLED',
    OTHER = 'OTHER',
    COLLECTION_UPDATE = 'COLLECTION_UPDATE', // Backend-only reason — not selectable by users. Used when appointments are cancelled as part of a series update operation.
}

export interface Appointment {
    id: string; // uuid or dc_schedule_id or data_cur_event_id
    user_id: string; // Eterno user id or instance_id::pms_user_id
    is_eterno_user?: boolean;
    collection_id?: string;
    chain_configuration_id?: string;
    collection_data?: {
        position: number;
        total_appointments: number;
        chain_name?: { en: string; de: string };
    };
    day: string; // ISO DATE: YYYY-MM-DD
    status: AppointmentBookStatus;
    location_key: string;
    is_online: boolean;
    customer_id: string;

    is_active: boolean; // True: In the Future & (Not Cancelled || Cancelled Confirmed) | False: Past || (Cancelled & Cancelled Confirmed)
    is_cancellation_confirmed: boolean; // False: default
    is_feedback_event_sent: boolean;

    name: string;
    description: string;

    start: string; // ISO DATETIME
    end: string; // ISO DATETIME
    duration: {
        value: number;
        units: string; // always MINUTES
    };

    professional: AppointmentProfessional;

    priority: Priority;

    cancel_reason?: AppointmentCancelReason; // API payload can include backend-only reasons (e.g. COLLECTION_UPDATE)
    is_no_show?: boolean; // if no show

    pvs_provider: PVSProvider;

    // DOC CIRRUS
    links?: {
        dc_instance_id?: string;

        patient_id: string; // doc_cirrus_user_id
        calendar_id: string;
        schedule_type_id: string; // doc_cirrus_schedule_type_id

        schedule_id?: string;
        practitioner_id?: string;
        location_id?: string;
        resource_id?: string; // TODO: PVS-MIGRATION rename because resource_id means something else for data_cur
        conference_id?: string;

        room_id?: string;

        online_url?: string;
        online_url_for_doctor?: string;
        ak_call_id?: string;
    };

    // DATA CUR
    data_cur_links?: {
        instance_id: string;

        patient_id: string;
        resource_id: string;
        event_type_id: string;
        event_type_code: string;
        event_id?: string;

        room_id?: string;

        online_url?: string;
        online_url_for_doctor?: string;
        ak_call_id?: string;
    };

    data: {
        patient_name?: string;
        generated_id?: string;

        documents: Document[];
        info: string;
        symptoms: Symptom[];

        services: AppointmentType[];

        is_for_self: boolean;
        is_returning: boolean;
        is_with_prescription: boolean;
        is_anamnesis_shared?: boolean; // TODO
        is_insurance_card_active: boolean;

        online_provider?: OnlineAppointmentProvider;

        insurance_coverage_type?: string; // public, private, selfpayer
    };

    is_external?: boolean;

    meta: {
        created_at: string;
        updated_at: string;
    };
}

export interface DailyAppointment extends Appointment {
    color?: string; // hex color

    patient?: {
        id: string; // "-" if not eterno user

        name: {
            first_name: string; // firstname
            middle_name?: string; // middlename
            last_name: string; // lastname
            salutation: string; // [ "MR", "MS", "NONE" ]
            gender: string; // [  "", "MALE", "FEMALE", "VARIOUS", "UNKNOWN", "UNDEFINED" ]
        };

        account: {
            date_of_birth: string; // "1985-11-13T09:00:00.000Z"
            patient_number?: string;
        };

        pvs: {
            instance_id: string;
            user_id: string;
        };

        contact: {
            email: string;
            phone?: string;
        };

        address?: PatientAddress;

        // TODO: PVS-MIGRATION ensure this change make sense before it was HealthInsurance type
        insurance?: {
            name: string;
            type: string;
        };
    };
}

export interface AppointmentType {
    id?: string; // TODO: this is missing from patient book and cannot be used properly. Deprecated!
    key: string;
    name: string;
    is_mapped: boolean;
}

export interface AppointmentProfessional {
    id: string;
    key: string;
    type_key: string;
}

export enum OnlineAppointmentProvider {
    DOC_CIRRUS = 'doc-cirrus',
    ARZTKONSULTATION = 'arztkonsultation',
}

export interface Symptom {
    name: string;
    key?: string;
}

export enum Insurance {
    PUBLIC = 'GKV',
    PRIVATE = 'PKV',
    // TODO: decide which self payer key we will stick to
    SELFPAYER = 'Selbstzahler',
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    SELF_PAYER = 'Selbstzahler',
}

export enum Priority {
    NONE = 'NONE',
    LOW = 'LOW',
    NORMAL = 'NORMAL',
    IMPORTANT = 'IMPORTANT',
    VERYIMPORTANT = 'VERYIMPORTANT',
    CRITICAL = 'CRITICAL',
}

// TODO: move into translation
export enum PriorityDETranslation {
    NONE = 'Keine',
    LOW = 'Niedrig',
    NORMAL = 'Normal',
    IMPORTANT = 'Wichtig',
    VERYIMPORTANT = 'Sehr Wichtig',
    CRITICAL = 'Kritisch',
}

interface Requirement {
    status: IndicatorStatus;
    text: string;
}

export enum AppointmentStatus {
    IN_ROOM = 'Zimmer',
    ONLINE = 'Online',
    WAITING = 'Im Warteraum',
    CHECKING_IN = 'Im Check-in',
    EXPECTED = 'Erwartet',
    DONE = 'Fertig',
    CANCELLED = 'Abgesagt',
}

// TODO: To be merged with the `Appointment` interface
export interface AppointmentRow {
    id: string;
    patient_name: string;
    insurance: Insurance;
    practitioner: string;
    appointment_type: string;
    expected_time: string;
    arrival_time?: string;
    requirements: Requirement[];
    dc_profile: string;
    status: AppointmentStatus;
    room_number?: number;
}

export enum IntervalType {
    DAILY = 'DAILY',
    WEEKLY = 'WEEKLY',
    MONTHLY = 'MONTHLY',
}

export enum PreferredAvailability {
    MORNING = 'MORNING',
    AFTERNOON = 'AFTERNOON',
    FLEXIBLE = 'FLEXIBLE',
    FIXED = 'FIXED',
}

export enum SeriesPlanningMode {
    AUTO = 'AUTO',
    START_DATE = 'START_DATE',
}

export enum AvailabilityStatus {
    AVAILABLE = 'AVAILABLE',
    PROPOSED = 'PROPOSED',
    UNAVAILABLE = 'UNAVAILABLE',
}

export interface CollectionConfiguration {
    start_date: string; // YYYY-MM-DD
    interval_type: IntervalType;
    interval_frequency: number;
    preferred_availability: PreferredAvailability;
    preferred_time?: string; // HH:mm — only when preferred_availability is FIXED
    number_of_appointments: number;
    planning_mode?: SeriesPlanningMode; // undefined (legacy/edit) is treated as START_DATE
}

export interface CollectionOccurrence {
    id: string;
    start_datetime: string; // ISO DATETIME
    end_datetime: string; // ISO DATETIME
    availability: AvailabilityStatus;
    original_time?: string; // HH:mm - original requested time when availability is PROPOSED
}

export interface BuildCollectionOptions {
    isOverlap?: boolean;
}

export interface PlacedAppointment {
    id: string;
    position: number;
    startDatetime: string; // ISO datetime
    endDatetime: string;
    calendarId?: string;
    isPast?: boolean;
}

export enum CollectionStep {
    CONFIG = 'config',
    LIST = 'list',
}

export interface SeriesPlacementConfig {
    patientName: string;
    appointmentTypeName: string;
    calendarName: string;
    durationMinutes: number;
    totalAppointments: number;
    practitionerCalendarId: string;
    previousCalendarSelection: string[];
}

export interface SeriesStateSnapshot {
    isSeriesMode: boolean;
    collectionStep: CollectionStep;
    collection_configuration?: CollectionConfiguration;
    collection_occurrences: CollectionOccurrence[];
    collectionPastAppointments: AppointmentCollectionItem[];
}

export enum CollectionEditScope {
    SINGLE = 'single',
    ALL_FUTURE = 'all_future',
}

export enum CollectionType {
    SERIES = 'SERIES',
    CHAIN = 'CHAIN',
}

export enum CollectionStatus {
    PENDING = 'PENDING',
    SUCCESS = 'SUCCESS',
    FAILED = 'FAILED',
}

export interface AppointmentSetStatus {
    collection_id: string;
    status: CollectionStatus;
    appointments: {
        start_date: string;
        end_date: string;
        is_error?: boolean;
    }[];
}

export enum CollectionActionScope {
    SINGLE = 'single',
    ALL_FUTURE = 'all_future',
}

export type AppointmentCollectionItem = {
    id: string;
    start: string;
    end: string;
    status: AppointmentBookStatus;
    cancel_reason?: AppointmentCancelReason;
    user_id: string;
    customer_id: string;
};
